import { createRef, useMemo, useState, useEffect, forwardRef, useCallback } from 'react';
import type { PropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  defaultThemeProp,
  Flex,
  Icon,
  registerIcon,
  MenuButton,
  useConsolidatedRef,
  useI18n,
  menuHelpers,
  Progress,
  useItemIntersection,
  usePrevious,
  EmptyState,
  calculateFontSize,
  StyledText,
  useArrows,
  debounce,
  useLiveLog
} from '@pega/cosmos-react-core';
import type { ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';
import * as filterIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/filter.icon';

import type { EmailSummaryItemProps, EmailSummaryListProps } from './Email.types';
import EmailSummaryItem from './EmailSummaryItem';
import { StyledEmailSummaryList, StyledLoadMore } from './Email.styles';

registerIcon(filterIcon);

const StyledEmailSummaryListContainer = styled(Flex)(({ theme }) => {
  return css`
    height: 100%;
    background-color: ${theme.base.palette['primary-background']};
  `;
});
StyledEmailSummaryListContainer.defaultProps = defaultThemeProp;

const StyledEmailSummaryListHeader = styled.div(
  ({
    theme: {
      base: { spacing }
    }
  }) => {
    return css`
      padding: ${spacing} calc(2 * ${spacing});
    `;
  }
);
StyledEmailSummaryListHeader.defaultProps = defaultThemeProp;

const StyledActionWrapper = styled(Flex)(({ theme }) => {
  return css`
    padding-block-start: ${theme.base.spacing};

    & > ${StyledText} {
      white-space: nowrap;
    }
  `;
});
StyledActionWrapper.defaultProps = defaultThemeProp;

const StyledFilterButton = styled(Button)`
  margin-inline-start: auto;
`;

const StyledWorklist = styled(MenuButton)(({ theme }) => {
  const { 'font-size': fontSize, 'font-scale': fontScale } = theme.base;
  const fontSizes = calculateFontSize(fontSize, fontScale);
  return css`
    ${StyledText} {
      font-weight: ${theme.base['font-weight']['semi-bold']};
      font-size: ${fontSizes.m};
    }
  `;
});

StyledWorklist.defaultProps = defaultThemeProp;

const EmailSummaryList: ForwardRefForwardPropsComponent<EmailSummaryListProps> = forwardRef(
  function EmailSummaryList(
    {
      loading = false,
      onLoadMore,
      categories,
      onCategoryClick,
      items,
      count,
      filters,
      onFilterChange,
      onItemClick,
      emptyMessage,
      currentItemId,
      selectable = true,
      searchQuery,
      ...restProps
    }: PropsWithoutRef<EmailSummaryListProps>,
    ref: EmailSummaryListProps['ref']
  ) {
    const t = useI18n();
    const tablistRef = useConsolidatedRef(ref);
    const tabRefs = items.map(() => createRef<HTMLLIElement>());
    const prevEmail = usePrevious(items);
    const { announcePolite } = useLiveLog();

    const countMessage = useMemo(() => {
      if (count) {
        const { total, unread } = count;
        return unread
          ? `${t('unread_email_count', [unread, total])}`
          : `${t('results_count', [total], { count: total })}`;
      }
    }, [count]);
    const [announcement, setAnnouncement] = useState('');
    useArrows(
      tablistRef,
      {
        cycle: true,
        selector: ':scope > li',
        dir: 'up-down',
        updateTabIndex: !currentItemId
      },
      [items]
    );

    const buttonText = useMemo(() => {
      if (categories)
        return (
          menuHelpers.getSelected(categories)[0]?.primary ??
          menuHelpers.flatten(categories)[0].primary
        );
    }, [categories]);

    const handleLoadMore = useCallback(
      debounce(() => {
        if (!loading) {
          onLoadMore?.();
        }
      }, 200),
      [loading]
    );

    useItemIntersection(tablistRef, items.length - 1, handleLoadMore, ':scope > li');

    useEffect(() => {
      if (loading) setAnnouncement(t('loading_email'));
    }, [loading]);

    useEffect(() => {
      if (prevEmail) {
        const difference = items.length - prevEmail.length;
        if (difference) setAnnouncement(t('new_emails_count', [difference], { count: difference }));
      }
    }, [items]);

    useEffect(() => {
      if (tabRefs.length > 0 && currentItemId) {
        tabRefs[items.findIndex(item => item.id === currentItemId)]?.current?.focus();
      }
    }, [currentItemId]);

    useEffect(() => {
      if (announcement) {
        announcePolite({
          message: announcement
        });
      }
    }, [announcement]);

    return (
      <StyledEmailSummaryListContainer container={{ direction: 'column' }} {...restProps}>
        {!!(categories?.length || searchQuery) && (
          <StyledEmailSummaryListHeader>
            <StyledActionWrapper
              container={{ justify: 'space-between', alignItems: 'center', gap: 0.5 }}
            >
              <Flex item={{ shrink: 1 }}>
                {searchQuery ? (
                  <StyledText variant='h3'>{t('search_results')}</StyledText>
                ) : (
                  <StyledWorklist
                    text={buttonText}
                    variant='text'
                    popover={{ portal: true }}
                    menu={{
                      mode: 'single-select',
                      items: categories,
                      onItemClick: onCategoryClick
                    }}
                  />
                )}
              </Flex>
              {countMessage && <StyledText variant='secondary'>{countMessage}</StyledText>}
              {filters && (
                <StyledFilterButton variant='simple' label={t('email_filters')} icon>
                  <Icon name='filter' />
                </StyledFilterButton>
              )}
            </StyledActionWrapper>
          </StyledEmailSummaryListHeader>
        )}

        {items.length > 0 ? (
          <>
            <Flex
              role={selectable ? 'tablist' : undefined}
              container={{ direction: 'column' }}
              as={StyledEmailSummaryList}
              ref={tablistRef}
            >
              {items.map((emailItem, i) => {
                const { id, ...emailItemProps } = emailItem;
                return (
                  <EmailSummaryItem
                    searchQuery={searchQuery}
                    ref={tabRefs[i]}
                    id={id}
                    key={id}
                    {...emailItemProps}
                    onSelect={(selectedItemId: EmailSummaryItemProps['id']) => {
                      onItemClick(selectedItemId);
                    }}
                    role={selectable ? 'tab' : undefined}
                    active={selectable ? currentItemId === id : undefined}
                  />
                );
              })}
            </Flex>
            {loading && (
              <Flex container={{ justify: 'center' }} as={StyledLoadMore}>
                <Progress placement='inline' message={t('loading_email')} />
              </Flex>
            )}
          </>
        ) : (
          <EmptyState message={emptyMessage} />
        )}
      </StyledEmailSummaryListContainer>
    );
  }
);

export default EmailSummaryList;
